import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import { User, Role, Manager, Team } from "@/types";
import { useToast } from "@/components/ui/use-toast";
import { logUserActivity } from "@/utils/activityLogger";
import { useTenant } from "@/contexts/TenantContext";

export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [deletedUsers, setDeletedUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletedUsersLoading, setDeletedUsersLoading] = useState(false);
  const [addUserError, setAddUserError] = useState<string | null>(null);
  const [lastUsersFetch, setLastUsersFetch] = useState<number>(0);
  const [lastDeletedUsersFetch, setLastDeletedUsersFetch] = useState<number>(0);
  const { toast } = useToast();
  const { currentTenant } = useTenant();

  const fetchUsers = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (loading) {
      return;
    }

    // Rate limiting: don't fetch if we just fetched within the last 1 second
    const now = Date.now();
    if (now - lastUsersFetch < 1000) {
      return;
    }

    try {
      setLoading(true);
      setLastUsersFetch(now);

      // Get current tenant ID from cached context
      const tenantId = currentTenant?.tenant_id;
      if (!tenantId) {
        console.error("No tenant ID available");
        toast({
          title: "Error",
          description: "No tenant ID available.",
          variant: "destructive",
        });
        return;
      }

      const { data: usersData, error: usersError } = await supabase
        .from("users")
        .select(
          `
          id,
          first_name,
          last_name,
          email,
          phone_number,
          role_id,
          thryve_manager_id,
          onboarding_required,
          commercial_or_residential,
          sunstone_access,
          is_active,
          created_at,
          updated_at,
          role_details:roles(id, name)
        `
        )
        .eq("tenant_id", tenantId)
        .eq("is_active", true)
        .order("first_name");

      if (usersError) {
        toast({
          title: "Error",
          description: "Failed to load users.",
          variant: "destructive",
        });
        return;
      }

      const { data: userTeamsData, error: userTeamsError } = await supabase
        .from("user_teams")
        .select(
          `
          user_id,
          team_id,
          teams!inner(id, name)
        `
        )
        .in(
          "user_id",
          usersData.map((u) => u.id)
        );

      if (userTeamsError) {
        toast({
          title: "Error",
          description: "Failed to load user teams.",
          variant: "destructive",
        });
        return;
      }

      // Fetch installer partner assignments from junction table
      const {
        data: userInstallerPartnersData,
        error: userInstallerPartnersError,
      } = await supabase.from("user_installer_partners").select(`
          user_id,
          installer_partner_id,
          installer_partners!inner(
            id,
            company_name
          )
        `);

      if (userInstallerPartnersError) {
        toast({
          title: "Error",
          description: "Failed to load user installer partner assignments.",
          variant: "destructive",
        });
        return;
      }

      const usersWithTeams =
        usersData?.map((user) => {
          const userTeams =
            userTeamsData?.filter((ut) => ut.user_id === user.id) || [];
          const userInstallerAssignments =
            userInstallerPartnersData?.filter(
              (uip) => uip.user_id === user.id
            ) || [];

          // Extract installer partner data from the junction table
          const userInstallerPartners = userInstallerAssignments.map(
            (assignment) => {
              const installerPartner = Array.isArray(
                assignment.installer_partners
              )
                ? assignment.installer_partners[0]
                : assignment.installer_partners;
              return {
                id: String(
                  installerPartner?.id || assignment.installer_partner_id
                ),
                company_name:
                  installerPartner?.company_name || "Unknown Company",
              };
            }
          );

          // Combine sales team IDs and installer partner IDs for compatibility
          const salesTeamIds = userTeams.map((ut) => String(ut.team_id));
          const installerPartnerIds = userInstallerPartners.map((ip) =>
            String(ip.id)
          );
          const allTeamIds = [...salesTeamIds, ...installerPartnerIds];

          return {
            ...user,
            user_teams: userTeams.map((ut) => {
              // Handle both single object and array cases from Supabase
              const teamData = Array.isArray(ut.teams) ? ut.teams[0] : ut.teams;
              return {
                team_id: String(ut.team_id),
                teams: {
                  id: String(teamData?.id || ""),
                  name: String(teamData?.name || ""),
                },
              };
            }),
            installer_partners: userInstallerPartners,
            team_ids: allTeamIds,
            // Map database fields to frontend interface
            require_onboarding: user.onboarding_required,
            create_in_thryve: false, // Default to false since this field doesn't exist in DB
            // Fix role_details to match User interface
            role_details: (() => {
              if (!user.role_details) return undefined;
              if (
                Array.isArray(user.role_details) &&
                user.role_details.length > 0
              ) {
                const firstRole = user.role_details[0] as any;
                return {
                  id: String(firstRole.id),
                  name: String(firstRole.name),
                };
              }
              if (
                typeof user.role_details === "object" &&
                !Array.isArray(user.role_details)
              ) {
                const roleDetails = user.role_details as any;
                return {
                  id: String(roleDetails.id),
                  name: String(roleDetails.name),
                };
              }
              return undefined;
            })(),
          };
        }) || [];

      setUsers(usersWithTeams);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load users.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, currentTenant?.tenant_id]);

  const fetchDeletedUsers = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (deletedUsersLoading) {
      return;
    }

    // Rate limiting: don't fetch if we just fetched within the last 1 second
    const now = Date.now();
    if (now - lastDeletedUsersFetch < 1000) {
      return;
    }

    try {
      setDeletedUsersLoading(true);
      setLastDeletedUsersFetch(now);

      // Get current tenant ID from cached context
      const tenantId = currentTenant?.tenant_id;
      if (!tenantId) {
        console.error("No tenant ID available");
        toast({
          title: "Error",
          description: "No tenant ID available.",
          variant: "destructive",
        });
        return;
      }

      const { data: deletedUsersData, error: deletedUsersError } =
        await supabase
          .from("users")
          .select(
            `
          id,
          email,
          first_name,
          last_name,
          deleted_at,
          deleted_by,
          original_email,
          original_first_name,
          original_last_name,
          created_at
        `
          )
          .eq("tenant_id", tenantId)
          .eq("is_active", false)
          .not("deleted_at", "is", null)
          .order("deleted_at", { ascending: false });

      if (deletedUsersError) {
        console.error("Failed to fetch deleted users:", deletedUsersError);
        // Only show toast for non-network errors to prevent spam
        if (!deletedUsersError.message?.includes("INSUFFICIENT_RESOURCES")) {
          toast({
            title: "Error",
            description: "Failed to load deleted users.",
            variant: "destructive",
          });
        }
        return;
      }

      setDeletedUsers(deletedUsersData || []);
    } catch (error: any) {
      console.error("Failed to fetch deleted users:", error);
      // Only show toast for non-network errors to prevent spam
      if (
        !error?.message?.includes("INSUFFICIENT_RESOURCES") &&
        !error?.name?.includes("NetworkError")
      ) {
        toast({
          title: "Error",
          description: "Failed to load deleted users.",
          variant: "destructive",
        });
      }
    } finally {
      setDeletedUsersLoading(false);
    }
  }, [toast, currentTenant?.tenant_id]);

  const fetchRoles = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("roles")
        .select("*")
        .order("name");

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load roles.",
          variant: "destructive",
        });
        return;
      }
      setRoles(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load roles.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const fetchManagers = useCallback(async () => {
    try {
      // Query users table instead of non-existent user_managers table
      const { data, error } = await supabase
        .from("users")
        .select("id, first_name, last_name, email, role_id")
        .eq("is_active", true)
        .order("first_name");

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load managers.",
          variant: "destructive",
        });
        return;
      }
      setManagers(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load managers.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const fetchTeams = useCallback(async () => {
    try {
      // Get current tenant ID
      const { data: tenantId, error: tenantError } = await supabase.rpc(
        "get_current_user_tenant_id"
      );
      if (tenantError) {
        console.error("Error getting tenant ID:", tenantError);
        toast({
          title: "Error",
          description: "Failed to get tenant ID.",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase
        .from("teams")
        .select(
          "id, name, type, manager_id, biz_dev_id, empower_created, ses_created"
        )
        .eq("tenant_id", tenantId)
        .order("name");

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load teams.",
          variant: "destructive",
        });
        return;
      }

      // Transform data to include manager_name and biz_dev_rep_name
      const userIds = new Set<string>();
      data.forEach((team) => {
        if (team.manager_id) userIds.add(team.manager_id);
        if (team.biz_dev_id) userIds.add(team.biz_dev_id);
      });

      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("id, first_name, last_name")
        .in("id", Array.from(userIds));

      if (userError) {
        toast({
          title: "Error",
          description: "Failed to load user details for teams.",
          variant: "destructive",
        });
        return;
      }

      const userMap = new Map<
        string,
        { first_name: string; last_name: string }
      >();
      userData.forEach((user) => {
        userMap.set(user.id, {
          first_name: user.first_name,
          last_name: user.last_name,
        });
      });

      const transformedData = data.map((team) => ({
        ...team,
        manager_name:
          team.manager_id && userMap.get(team.manager_id)
            ? `${userMap.get(team.manager_id)!.first_name} ${userMap.get(team.manager_id)!.last_name}`
            : "N/A",
        biz_dev_rep_name:
          team.biz_dev_id && userMap.get(team.biz_dev_id)
            ? `${userMap.get(team.biz_dev_id)!.first_name} ${userMap.get(team.biz_dev_id)!.last_name}`
            : "N/A",
        empower_created: team.empower_created || false,
        ses_created: team.ses_created || false,
      }));

      setTeams(transformedData || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load teams.",
        variant: "destructive",
      });
    }
  }, [toast, currentTenant?.tenant_id]);

  // Initial data fetch on mount and when tenant changes
  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchManagers();
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.tenant_id]); // Re-fetch when tenant changes

  interface CreateUserData extends Omit<User, "id" | "team_id"> {
    manager_id?: string | null;
    team_ids?: string[]; // Use team_ids to match formData
    commercial_or_residential?: "residential" | "commercial";
    residentialStates?: string[];
    commercialStates?: string[];
  }

  const createUser = async (userData: CreateUserData) => {
    try {
      const functionsBaseUrl =
        import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || "/functions/v1";

      const sessionResult = await supabase.auth.getSession();
      const session = sessionResult.data.session;

      if (!session) {
        toast({
          title: "Error",
          description: "You must be logged in to perform this action.",
          variant: "destructive",
        });
        return { success: false };
      }

      const payload = {
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        role_id: userData.role_id,
        phone_number: userData.phone_number || "",
        team_ids: userData.team_ids || [],
        password: userData.password || undefined,
        thryve_manager_id: userData.manager_id || null,
        commercial_or_residential:
          userData.commercial_or_residential || "residential",
        require_onboarding: userData.require_onboarding,
        sunstone_access: userData.sunstone_access,
        target_tenant_id: currentTenant?.tenant_id, // Pass the current tenant context
      };

      const response = await fetch(`${functionsBaseUrl}/create-admin-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        setAddUserError(data.error || "Failed to create user.");
        toast({
          title: "Error",
          description: data.error || "Failed to create user.",
          variant: "destructive",
        });
        return { success: false };
      }

      // Save allowed states for the new user if they were provided
      if (data.user && currentTenant?.tenant_id) {
        const newUserId = data.user.id;

        // Check if the user has custom states in userData
        if (userData.residentialStates || userData.commercialStates) {
          const defaultResidentialStates = ['AR', 'AZ', 'CT', 'CO', 'IA', 'KS', 'KY', 'LA', 'MA', 'ME', 'MO', 'NC', 'NH', 'NJ', 'OH', 'OK', 'PA', 'RI', 'SC', 'TX', 'VT', 'MD', 'FL'];
          const defaultCommercialStates = ['CT', 'IL', 'MD', 'MA', 'NJ', 'PA', 'RI', 'VA', 'DC', 'WV', 'ME', 'NH', 'VT'];

          const statesToSave = {
            user_id: newUserId,
            tenant_id: currentTenant.tenant_id,
            residential_states: userData.residentialStates || defaultResidentialStates,
            commercial_states: userData.commercialStates || defaultCommercialStates,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          const { error: statesError } = await supabase
            .from('allowed_user_states')
            .insert(statesToSave);

          if (statesError) {
            console.error('Error saving user allowed states:', statesError);
          }
        } else {
          // Insert default states for new user
          const defaultResidentialStates = ['AR', 'AZ', 'CT', 'CO', 'IA', 'KS', 'KY', 'LA', 'MA', 'ME', 'MO', 'NC', 'NH', 'NJ', 'OH', 'OK', 'PA', 'RI', 'SC', 'TX', 'VT', 'MD', 'FL'];
          const defaultCommercialStates = ['CT', 'IL', 'MD', 'MA', 'NJ', 'PA', 'RI', 'VA', 'DC', 'WV', 'ME', 'NH', 'VT'];

          const { error: statesError } = await supabase
            .from('allowed_user_states')
            .insert({
              user_id: newUserId,
              tenant_id: currentTenant.tenant_id,
              residential_states: defaultResidentialStates,
              commercial_states: defaultCommercialStates,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });

          if (statesError) {
            console.error('Error saving default user allowed states:', statesError);
          }
        }
      }

      await fetchUsers(); // Refresh users
      setAddUserError(null);

      // Send Sunstone email if Sunstone access is enabled for new user
      if (userData.sunstone_access) {
        try {
          // Get team name from team_ids
          let teamName = "Unknown Team";
          if (userData.team_ids && userData.team_ids.length > 0) {
            const teamId = userData.team_ids[0];
            const team = teams.find((t) => String(t.id) === String(teamId));
            teamName = team?.name || "Unknown Team";
          }

          const emailResponse = await fetch(
            `${functionsBaseUrl}/sunstone-email`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                first_name: userData.first_name,
                last_name: userData.last_name,
                email: userData.email,
                phone: userData.phone_number || "",
                team_name: teamName,
              }),
            }
          );

          if (emailResponse.ok) {
            toast({
              title: "Success",
              description:
                "User created and Sunstone account creation email sent successfully!",
            });
          } else {
            const emailError = await emailResponse.json();
            console.error("Sunstone email error:", emailError);
            toast({
              title: "Partial Success",
              description:
                "User created successfully, but failed to send Sunstone email. Please contact support.",
              variant: "destructive",
            });
          }
        } catch (emailError) {
          console.error("Failed to send Sunstone email:", emailError);
          toast({
            title: "Partial Success",
            description:
              "User created successfully, but failed to send Sunstone email. Please contact support.",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Success",
          description: "User created successfully.",
        });
      }

      // Log the user creation activity (don't let this break the user creation)
      if (data.user?.id) {
        try {
          await logUserActivity.created(data.user.id, {
            email: userData.email,
            first_name: userData.first_name,
            last_name: userData.last_name,
            role_id: userData.role_id,
          });
        } catch (logError) {
          console.warn("Failed to log user creation activity:", logError);
          // Don't fail the user creation process if logging fails
        }
      }

      return { success: true, user: data.user };
    } catch (error: any) {
      setAddUserError(error?.message || "Failed to create user.");
      toast({
        title: "Error",
        description: error?.message || "Failed to create user.",
        variant: "destructive",
      });
      return { success: false };
    }
  };

  const updateUser = async (user: User) => {
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        toast({
          title: "Error",
          description: "You must be logged in to perform this action.",
          variant: "destructive",
        });
        return { success: false };
      }

      // Check if we're enabling Sunstone access (new value is true and old value was false or undefined)
      const currentUser = users.find((u) => u.id === user.id);
      const isEnablingSunstoneAccess =
        user.sunstone_access === true && currentUser?.sunstone_access !== true;

      const updatePayload = {
        ...user,
        // Map frontend fields to database fields
        onboarding_required: user.require_onboarding,
        // Remove fields that don't exist in database
        create_in_thryve: undefined,
        require_onboarding: undefined,
      };

      console.log('Update payload being sent:', {
        userId: user.id,
        require_onboarding: user.require_onboarding,
        onboarding_required: updatePayload.onboarding_required,
        payload: updatePayload
      });

      const functionsBaseUrl =
        import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || "/functions/v1";
      const response = await fetch(`${functionsBaseUrl}/update-admin-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.data.session.access_token}`,
        },
        body: JSON.stringify(updatePayload),
      });
      const data = await response.json();

      if (!response.ok) {
        toast({
          title: "Error",
          description: data.error || "Failed to update user.",
          variant: "destructive",
        });
        return { success: false };
      }
      await fetchUsers(); // Refresh users

      // Send Sunstone email if we're enabling Sunstone access
      if (isEnablingSunstoneAccess) {
        try {
          // Get user's team information for the email
          const userWithTeams = users.find((u) => u.id === user.id);
          const teamName =
            userWithTeams?.user_teams?.[0]?.teams?.name || "Unknown Team";

          const emailResponse = await fetch(
            `${functionsBaseUrl}/sunstone-email`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${session.data.session.access_token}`,
              },
              body: JSON.stringify({
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                phone: user.phone_number || "",
                team_name: teamName,
              }),
            }
          );

          if (emailResponse.ok) {
            toast({
              title: "Success",
              description:
                "User updated and Sunstone account creation email sent successfully!",
            });
          } else {
            const emailError = await emailResponse.json();
            console.error("Sunstone email error:", emailError);
            toast({
              title: "Partial Success",
              description:
                "User updated successfully, but failed to send Sunstone email. Please contact support.",
              variant: "destructive",
            });
          }
        } catch (emailError) {
          console.error("Failed to send Sunstone email:", emailError);
          toast({
            title: "Partial Success",
            description:
              "User updated successfully, but failed to send Sunstone email. Please contact support.",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Success",
          description: "User updated successfully!",
        });
      }

      // Log the user update activity (don't let this break the user update)
      try {
        await logUserActivity.updated(user.id, {
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          role_id: user.role_id,
        });
      } catch (logError) {
        console.warn("Failed to log user update activity:", logError);
        // Don't fail the user update process if logging fails
      }

      return { success: true };
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to update user.",
        variant: "destructive",
      });
      return { success: false };
    }
  };

  const deleteUser = async (userId: string, isHardDelete: boolean = false) => {
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        toast({
          title: "Error",
          description: "You must be logged in to perform this action.",
          variant: "destructive",
        });
        return { success: false };
      }
      const functionsBaseUrl =
        import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || "/functions/v1";
      const response = await fetch(`${functionsBaseUrl}/delete-admin-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.data.session.access_token}`,
        },
        body: JSON.stringify({
          id: userId,
          hard_delete: isHardDelete,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        toast({
          title: "Error",
          description:
            data.error ||
            `Failed to ${isHardDelete ? "permanently delete" : "delete"} user.`,
          variant: "destructive",
        });
        return { success: false };
      }
      await fetchUsers(); // Refresh users
      if (!isHardDelete) {
        await fetchDeletedUsers(); // Refresh deleted users (only for soft delete)
      }

      // Log the user deletion activity
      await logUserActivity.deleted(userId, {
        deleted_user_id: userId,
        hard_delete: isHardDelete,
      });

      return { success: true };
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error?.message ||
          `Failed to ${isHardDelete ? "permanently delete" : "delete"} user.`,
        variant: "destructive",
      });
      return { success: false };
    }
  };

  return {
    users,
    deletedUsers,
    loading,
    deletedUsersLoading,
    roles,
    managers,
    teams,
    createUser,
    updateUser,
    deleteUser,
    fetchDeletedUsers,
    fetchUsers,
    fetchTeams,
    addUserError,
  };
};
